import IconLoader from "@/features/Common/components/IconLoader";
import Panel from "@/features/Common/components/Panel";
import { MembershipDependent, Member } from "@/services/core-api-adapter";
import { FaceIconBrand } from "@/theme/icons";
import { convertPxToRem } from "@/utils";
import {
  Typography,
  Stack,
  ListItem,
  IconButton,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";

interface MinorDependentsListProps {
  dependentsList: MembershipDependent[];
  onEditButtonClick: (dependent: MembershipDependent) => void;
  onDeleteButtonClick: (dependent: MembershipDependent) => void;
  isError: boolean;
}

export default function MinorDependentsList({
  dependentsList = [],
  onEditButtonClick,
  onDeleteButtonClick,
  isError,
}: MinorDependentsListProps) {
  const { t } = useTranslation();
  return (
    <Stack component="ul" spacing={2} aria-label="Minors" role="list">
      {!!dependentsList && !isError && (
        <>
          {(dependentsList || []).map((dependent, index) => {
            const fullName = `${dependent?.firstName} ${dependent?.lastName}`;
            const listItemKey = `${fullName}_${index}`;
            const dateOfBirth = dependent?.dateOfBirth;

            return (
              <ListItem
                key={listItemKey}
                sx={{ padding: 0 }}
                aria-label={fullName}
              >
                <Stack spacing={2} width="100%">
                  <Stack spacing={2} direction="row" width="100%">
                    <FaceIconBrand
                      sx={{
                        fontSize: (theme) => theme.spacing(4),
                        color: "accent1.light",
                      }}
                    />
                    <Stack width="100%" spacing={1}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Stack spacing={1} direction="row" alignItems="center">
                          <Stack
                            spacing={1}
                            direction="row"
                            alignContent="flex-start"
                            justifyContent="space-between"
                          >
                            <Typography
                              component="p"
                              variant="h4"
                              fontWeight="600"
                              sx={{ wordBreak: "break-word" }}
                            >
                              {fullName}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Stack
                          sx={{
                            backgroundColor: "accent1.main",
                            borderRadius: 1,
                            alignSelf: "center",
                            whiteSpace: "nowrap",
                          }}
                          padding={1}
                          marginLeft="auto"
                        >
                          <Typography
                            fontWeight={600}
                            variant="body2"
                            color="primary.contrastText"
                          >
                            {t("MyProfile.minorDependents.nonInsured.title")}
                          </Typography>
                        </Stack>
                      </Stack>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        mt={2}
                      >
                        <Stack>
                          <Typography variant="body2" color="GrayText">
                            {t("DateOfBirthInput.label")}
                          </Typography>
                          <Typography>{dateOfBirth}</Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="flex-end"
                        >
                          <IconButton
                            onClick={() => onEditButtonClick(dependent)}
                            color="primary"
                            aria-label={t("common.editButton")}
                          >
                            <IconLoader icon="PenIcon" />
                          </IconButton>

                          <IconButton
                            onClick={() => onDeleteButtonClick(dependent)}
                            color="primary"
                            aria-label={t("common.removeButton")}
                          >
                            <IconLoader icon="BinIcon" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
                <Divider sx={{ mt: 2 }} />
              </ListItem>
            );
          })}
        </>
      )}

      {isError && (
        <Panel>
          <Typography variant="h4" align="center">
            {t("common.somethingWentWrong")}
          </Typography>
        </Panel>
      )}
    </Stack>
  );
}
